import { IRepository, IRepositoryEntry, IPagination, TKeyValuePair, ISchemaField } from '@/types';
import { fetchWrapper, IBaseResponse, extractPaginationInfo } from '../apiBase';
import { BASE_API_URL } from '@/config/environment';
import { stringify } from 'querystring';

import { IDataFrameResponse } from '../dataFrameTypes';
import { TDFRequestQueryParams } from '../dataFrameApi';
import { SYSTEM_ADDED_COLUMNS } from '@/components/DataFrame/consts';

const DEFAULT_PAGE_SIZE = 100;

interface IDataframeEditableResponse extends IBaseResponse {
  is_indexed: boolean;
}

const getRemoteDfBasePath = (workspaceId: string, repository: IRepository): string => {
  const { namespace, name: repositoryName } = repository;
  return `${BASE_API_URL}/repos/${namespace}/${repositoryName}/workspaces/${workspaceId}/data_frames`;
};

export const fetchDataframeEditableStatus = async ({
  repository,
  path,
  workspaceId,
  token,
}: {
  repository: IRepository;
  workspaceId?: string;
  path: string;
  token?: string;
}): Promise<IDataframeEditableResponse> => {
  if (!workspaceId) {
    return {
      is_indexed: false,
      status: 'error',
      status_message: 'No workspaceId provided',
      error: { title: 'No workspaceId provided', type: '' },
    };
  }
  const targetUrl = `${BASE_API_URL}/repos/${repository.namespace}/${repository.name}/workspaces/${workspaceId}/data_frames/resource/${path}`;
  // console.log('fetchDataframeEditableStatus > targetUrl', targetUrl);
  const data = await fetchWrapper({
    resourceName: 'openDataframeEditableStatus',
    targetUrl,
    authToken: token,
    resourceContext: { namespace: repository.namespace, repository: repository.name, workspaceId, path },
  });
  // console.log('fetchDataframeEditableStatus > data', data);

  return data;
};

export const indexRemoteDataframe = async ({
  repository,
  path,
  workspaceId,
  token,
}: {
  repository: IRepository;
  workspaceId: string;
  path: string;
  token?: string;
}): Promise<IDataframeEditableResponse> => {
  const targetUrl = `${BASE_API_URL}/repos/${repository.namespace}/${repository.name}/workspaces/${workspaceId}/data_frames/resource/${path}`;
  const payload = {
    is_indexed: true,
  };
  // console.log('indexRemoteDataframe > targetUrl', targetUrl, payload);

  const data = await fetchWrapper({
    method: 'PUT',
    contentType: 'application/json',
    resourceName: 'indexRemoteDataframe',
    targetUrl,
    authToken: token,
    body: JSON.stringify(payload),
    resourceContext: { namespace: repository.namespace, repository: repository.name, workspaceId, path },
  });

  console.log(data, 'this is data');
  return data;
};

const sanitizeQuery = (query: TDFRequestQueryParams) => {
  return Object.fromEntries(
    Object.entries(query).filter(([key, value]) => {
      return value !== undefined && value !== null && value !== '';
    }),
  );
};

type TFetchRemoteDataframeParams = {
  repository: IRepository;
  path: string;
  query: TDFRequestQueryParams;
  workspaceId: string;
  token?: string;
};

export const fetchRemoteDataframe = async ({
  repository,
  path,
  query,
  workspaceId,
  token,
}: TFetchRemoteDataframeParams): Promise<IDataFrameResponse> => {
  const { namespace, name: repositoryName } = repository;
  const queryWithDefaultPageSize = sanitizeQuery({ ...query, page_size: DEFAULT_PAGE_SIZE });
  const queryString = stringify(queryWithDefaultPageSize as any);
  const targetUrl = `${getRemoteDfBasePath(workspaceId, repository)}/resource/${path}?${queryString}`;
  // console.log('fetchRemoteDataframe > targetUrl', targetUrl);

  // console.log("yomamaaa")

  return await fetchWrapper({
    resourceName: 'dataFrame',
    targetUrl,
    authToken: token,
    resourceContext: { namespace, repositoryName },
    extra: {
      query: query,
    },
  });
};

export const getResultsDownloadUrl = ({
  repository,
  path,
  query,
  workspaceId,
  fileExtension = 'csv',
}: TFetchRemoteDataframeParams & { fileExtension?: string }): string => {
  const queryWithDefaultPageSize = sanitizeQuery({ ...query, page_size: DEFAULT_PAGE_SIZE });
  const queryString = stringify(queryWithDefaultPageSize as any);
  const baseFilename = path.split('/').pop()?.split('.').shift();
  const outputFilename = `${baseFilename}.${fileExtension}`;
  const targetUrl = `${getRemoteDfBasePath(workspaceId, repository)}/download/${path}?${queryString}&output=${outputFilename}`;
  return targetUrl;
};

export const fetchRemoteDataframeDiff = async ({
  repository,
  path,
  query,
  workspaceId,
  token,
}: TFetchRemoteDataframeParams): Promise<IDataFrameResponse> => {
  const { namespace, name: repositoryName } = repository;
  const queryWithDefaultPageSize = sanitizeQuery({ ...query, page_size: DEFAULT_PAGE_SIZE });
  const queryString = stringify(queryWithDefaultPageSize as any);
  const targetUrl = `${getRemoteDfBasePath(workspaceId, repository)}/diff/${path}?${queryString}`;
  // console.log('fetchRemoteDataframeDiff > targetUrl', targetUrl);
  return await fetchWrapper({
    resourceName: 'dataFrame',
    targetUrl,
    authToken: token,
    resourceContext: { namespace, repositoryName },
    extra: {
      query: query,
    },
  });
};

type TAddRowParams = {
  repository: IRepository;
  path: string;
  workspaceId: string;
  token?: string;
};

export const addRowToRemoteDataframe = async ({
  repository,
  path,
  workspaceId,
  token,
}: TAddRowParams): Promise<IDataFrameResponse> => {
  const { namespace, name: repositoryName } = repository;
  const targetUrl = `${getRemoteDfBasePath(workspaceId, repository)}/rows/resource/${path}`;

  // console.log('fetchRemoteDataframe > targetUrl', targetUrl);
  return fetchWrapper({
    resourceName: 'dataFrame',
    method: 'POST',
    contentType: 'application/json',
    body: JSON.stringify({ data: {} }),
    targetUrl,
    authToken: token,
    resourceContext: { namespace, repositoryName },
  });
};

type TRevertRowChangesParams = {
  rowId: string;
  repository: IRepository;
  path: string;
  workspaceId: string;
  token?: string;
};

export const revertRowChangesFromRemoteDataframe = async ({
  rowId,
  repository,
  path,
  workspaceId,
  token,
}: TRevertRowChangesParams): Promise<IDataFrameResponse> => {
  const { namespace, name: repositoryName } = repository;
  const targetUrl = `${getRemoteDfBasePath(workspaceId, repository)}/rows/${rowId}/restore/${path}/`;

  // console.log('fetchRemoteDataframe > targetUrl', targetUrl);
  return fetchWrapper({
    resourceName: 'dataFrame',
    method: 'POST',
    contentType: 'application/json',
    targetUrl,
    authToken: token,
    resourceContext: { namespace, repositoryName },
  });
};

type TDeleteRowParams = {
  rowId: string;
  repository: IRepository;
  path: string;
  workspaceId: string;
  token?: string;
};

export const deleteRowFromRemoteDataframe = async ({
  rowId,
  repository,
  path,
  workspaceId,
  token,
}: TDeleteRowParams): Promise<IDataFrameResponse> => {
  const { namespace, name: repositoryName } = repository;
  const targetUrl = `${getRemoteDfBasePath(workspaceId, repository)}/rows/${rowId}/resource/${path}`;

  // console.log('fetchRemoteDataframe > targetUrl', targetUrl);
  return fetchWrapper({
    resourceName: 'dataFrame',
    method: 'DELETE',
    targetUrl,
    authToken: token,
    resourceContext: { namespace, repositoryName },
  });
};

const remmoveAttributesByKey = (obj: { [key: string]: any }, attrs: string[]): { [key: string]: any } => {
  return Object.keys(obj).reduce<{ [key: string]: any }>((newObj, key) => {
    if (!attrs.includes(key)) {
      newObj[key] = obj[key];
    }
    return newObj;
  }, {});
};

// Note: This function is used to remove empty attributes from the row object
// This should be removed once the backend is updated to handle empty attributes
const removeEmptyAttributes = (obj: { [key: string]: any }) => {
  return Object.fromEntries(Object.entries(obj).filter(([_, v]) => v != null));
};

const sanitizeRowForUpdate = (updatedRow: TKeyValuePair) => {
  return removeEmptyAttributes(remmoveAttributesByKey(updatedRow, SYSTEM_ADDED_COLUMNS));
};

type TUpdateRowParams = {
  repository: IRepository;
  path: string;
  rowId: string;
  rowData: TKeyValuePair;
  workspaceId: string;
  token?: string;
};

export const updateRemoteDataframeRow = async ({
  repository,
  path,
  rowId,
  rowData,
  workspaceId,
  token,
}: TUpdateRowParams): Promise<IDataFrameResponse> => {
  const { namespace, name: repositoryName } = repository;
  const payload = {
    data: sanitizeRowForUpdate(rowData),
  };
  const targetUrl = `${getRemoteDfBasePath(workspaceId, repository)}/rows/${rowId}/resource/${path}`;
  // console.log('updateRemoteDataframeRow > targetUrl', targetUrl, payload);

  return fetchWrapper({
    resourceName: 'dataFrame',
    method: 'PUT',
    targetUrl,
    contentType: 'application/json',
    body: JSON.stringify(payload),
    authToken: token,
    resourceContext: { namespace, repositoryName },
  });
};
